import React from 'react'
import { Container, Col, Row, Form, Button } from 'react-bootstrap'
import { DateTime } from 'luxon'
import { FormDate } from '../ui/Form/Date'
import { Grouping } from '../../constants/grouping'
import Select from 'react-select'
import { ClientGroup } from '../../../../back-end/common/clientGroup'
import { Staff } from '../../../../back-end/common/staff'
import { FormSelect, SelectOption } from '../ui/Form/Select'
import { AsyncFormSelect } from '../ui/Form/AsyncSelect'
import { Search } from 'react-feather'
import { Loading } from '../ui/Loading/Loading'
import { Filters } from '../../screens/wip/wip'

const groupingOptions = Object.values(Grouping)

interface IntroScreenProps {
	isLoading: boolean
	allStaff: Staff[] // No typing on staff for now, filter this later per select if types added
	familyGroups: ClientGroup[]
	jobCategories: string[]
	selectedJobOption: SelectOption<string> | null
	selectedClientOption: SelectOption<string> | null
	loadJobOptions: (input: string) => Promise<SelectOption<string>[]>
	loadClientOptions: (input: string) => Promise<SelectOption<string>[]>
	handleJobChange: (option: SelectOption<string> | null) => void
	handleClientChange: (option: SelectOption<string> | null) => void
	filters: Filters
	handleRunAnalysis: () => void
}

const IntroScreen = (props: IntroScreenProps) => {
	const isDisabled = React.useMemo(
		() =>
			props.filters.jobNumber === '' &&
			props.filters.jobType === '' &&
			props.filters.clientManagerId === '' &&
			props.filters.clientPartnerId === '' &&
			props.filters.familyGroupId === '' &&
			props.filters.clientId == '' &&
			props.filters.jobManagerId === '' &&
			props.filters.jobPartnerId === '' &&
			props.filters.jobStaffId === '',
		[props.filters]
	)

	const jobCategoryOptions = React.useMemo(
		() =>
			props.jobCategories
				.map((type) => ({ value: type, label: type ?? 'N/A' }))
				.sort((a, b) => (a.label < b.label ? -1 : 1)),
		[props.jobCategories]
	)

	return (
		<Container>
			<Row>
				<Col className="pt-5">
					<Row>
						<Col>
							<h2>Create WIP Analysis</h2>
							<span>Select an end date to show all unbilled WIP</span>
						</Col>
					</Row>
					{props.isLoading ? (
						<Row>
							<Col />
							<Col sm="auto">
								<Loading show />
							</Col>
							<Col />
						</Row>
					) : (
						<>
							<Row className="pt-2">
								<Col sm={5}>
									<FormDate
										name={'endDate'}
										label="End Date"
										value={props.filters.endDate.toISODate()}
										onChange={(e) => props.filters.setEndDate(DateTime.fromISO(e.target.value))}
									/>
								</Col>
								<Col sm={5}>
									<Form.Label>Grouping</Form.Label>
									<Select
										name="grouping"
										onChange={(e) => props.filters.setGrouping(e as Grouping)}
										options={groupingOptions}
										value={props.filters.grouping}
										isSearchable={false}
									/>
								</Col>
							</Row>
							<Row className="pt-4">
								<Col>
									<span>Filter your WIP report by using one or more options</span>
								</Col>
							</Row>
							<Row className="pt-3">
								<Col sm={5}>
									<FormSelect
										name="jobType"
										label="Job Category"
										placeholder={<Placeholder object="job category" />}
										onChange={(e) => props.filters.setJobType(e ? (e as SelectOption<string>).value : '')}
										options={jobCategoryOptions}
										value={props.filters.jobType}
										isClearable
									/>
								</Col>
								<Col sm={5}>
									<AsyncFormSelect
										name="jobNumber"
										label="Job"
										placeholder={<Placeholder object="job" />}
										loadOptions={props.loadJobOptions}
										value={props.selectedJobOption}
										onChange={props.handleJobChange}
										isClearable
									/>
								</Col>
							</Row>
							<Row className="pt-3">
								<Col sm={5}>
									<FormSelect
										name="familyGroup"
										label="Group"
										placeholder={<Placeholder object="group" />}
										onChange={(e) => props.filters.setFamilyGroupId(e ? (e as SelectOption<string>).value : '')}
										options={props.familyGroups.map((fg) => ({ value: fg.xpmID, label: fg.name }))}
										value={props.filters.familyGroupId}
										isClearable
									/>
								</Col>
								<Col sm={5}>
									<AsyncFormSelect
										name="clientId"
										label="Client"
										placeholder={<Placeholder object="client" />}
										loadOptions={props.loadClientOptions}
										value={props.selectedClientOption}
										onChange={props.handleClientChange}
										isClearable
									/>
								</Col>
							</Row>
							<Row className="pt-3">
								<Col sm={5}>
									<FormSelect
										name="clientPartner"
										label="Client Partner"
										placeholder={<Placeholder object="client partner" />}
										onChange={(e) => props.filters.setClientPartnerId(e ? (e as SelectOption<string>).value : '')}
										options={props.allStaff.map((fg) => ({ value: fg.xpmID, label: fg.name }))}
										value={props.filters.clientPartnerId}
										isClearable
									/>
								</Col>
								<Col sm={5}>
									<FormSelect
										name="clientManager"
										label="Client Manager"
										placeholder={<Placeholder object="client manager" />}
										onChange={(e) => props.filters.setClientManagerId(e ? (e as SelectOption<string>).value : '')}
										options={props.allStaff.map((fg) => ({ value: fg.xpmID, label: fg.name }))}
										value={props.filters.clientManagerId}
										isClearable
									/>
								</Col>
							</Row>
							<Row className="pt-3">
								<Col sm={5}>
									<FormSelect
										name="jobPartner"
										label="Job Partner"
										placeholder={<Placeholder object="job partner" />}
										onChange={(e) => props.filters.setJobPartnerId(e ? (e as SelectOption<string>).value : '')}
										options={props.allStaff.map((fg) => ({ value: fg.xpmID, label: fg.name }))}
										value={props.filters.jobPartnerId}
										isClearable
									/>
								</Col>
								<Col sm={5}>
									<FormSelect
										name="jobManager"
										label="Job Manager"
										placeholder={<Placeholder object="job manager" />}
										onChange={(e) => props.filters.setJobManagerId(e ? (e as SelectOption<string>).value : '')}
										options={props.allStaff.map((fg) => ({ value: fg.xpmID, label: fg.name }))}
										value={props.filters.jobManagerId}
										isClearable
									/>
								</Col>
							</Row>
							<Row className="pt-3">
								<Col sm={5}>
									<FormSelect
										name="jobStaff"
										label="Time Recorded by Staff"
										placeholder={<Placeholder object="job staff" />}
										onChange={(e) => props.filters.setJobStaffId(e ? (e as SelectOption<string>).value : '')}
										options={props.allStaff.map((fg) => ({ value: fg.xpmID, label: fg.name }))}
										value={props.filters.jobStaffId}
										isClearable
									/>
								</Col>
							</Row>
							<Row className="pt-3">
								<Col>
									<Button variant="primary" onClick={props.handleRunAnalysis} disabled={isDisabled}>
										{isDisabled ? 'Select one filter to continue' : 'Run Analysis'}
									</Button>
								</Col>
							</Row>
						</>
					)}
				</Col>
			</Row>
		</Container>
	)
}

interface PlaceholderProps {
	object: string
}

const Placeholder = (props: PlaceholderProps) => {
	return (
		<div className="d-flex align-items-center">
			<Search size={14} />
			<span className="pl-2">Search or select a {props.object}</span>
		</div>
	)
}

export default IntroScreen
